// Identity fixtures: who the demo operator is, and the tenant record the console
// renders branding + feature flags from. All tenant-specific values come from the
// active client config (see ../clients).

import type { CallerScope } from '@parkify/api-types'
import { getPermissionsForRole } from '../../auth/permissions'
import {
  DEMO_ACCENT, DEMO_CLIENT, DEMO_CLIENT_ID, DEMO_CLIENT_NAME, DEMO_ROLE,
  DEMO_STAFF, DEMO_STAFF_USER, demoZoneScope,
  DEMO_TIMEZONE, DEMO_USER_EMAIL, DEMO_USER_NAME,
} from '../constants'
import { isoDaysFromNow } from '../time'

// Every per-tenant feature on so the demo shows the full operator surface.
const DEMO_FEATURES = {
  support: true, transactions: true, disputes: true, zones: true, qr: true,
  permits: true, validations: true, enforcement: true, accounting: true,
  analytics: true, customers: true, subscribers: true, avery: true,
  // Reserved bays are ON in the demo (phases 1–3 are demoable); production
  // clients default OFF until the feature is finished.
  reservedBays: true,
}

// ClientContext the sidebar + branding read (subset returned inside /me).
export const demoClientContext = {
  id: DEMO_CLIENT_ID,
  name: DEMO_CLIENT_NAME,
  accentColor: DEMO_ACCENT,
  logoUrl: DEMO_CLIENT.logoUrl,
  iconUrl: DEMO_CLIENT.iconUrl,
  features: DEMO_FEATURES,
}

// Response for GET /api/admin/users/me — drives role, permissions + branding.
//
// In staff mode the operator is Parkify-internal: `clientId: null` is the whole
// gate (App.tsx blocks PARKIFY_STAFF_ONLY screens on `clientId != null`), and
// `client: null` with it, because a staff operator is not scoped to a tenant and
// handing one a client context would re-skin the console as that tenant's.
export function demoMe() {
  if (DEMO_STAFF) {
    return {
      email: DEMO_STAFF_USER.email,
      name: DEMO_STAFF_USER.name,
      role: DEMO_STAFF_USER.role,
      clientId: null,
      permissions: getPermissionsForRole(DEMO_STAFF_USER.role),
      client: null,
      scope: { clientId: null, tenantId: null, zoneIds: null } satisfies CallerScope,
    }
  }
  return {
    email: DEMO_USER_EMAIL,
    name: DEMO_USER_NAME,
    role: DEMO_ROLE,
    clientId: DEMO_CLIENT_ID,
    permissions: getPermissionsForRole(DEMO_ROLE),
    client: demoClientContext,
    // The demo operator is client-wide, as the Worker reports an unpinned
    // client user, unless VITE_DEMO_ZONE_SCOPE pins it for QA. Without this the
    // console would read scope as unknown and (correctly) hide every location
    // write control in the demo.
    scope: { clientId: DEMO_CLIENT_ID, tenantId: null, zoneIds: demoZoneScope() } satisfies CallerScope,
  }
}

// Full Client record for GET /api/clients/me and the Client Admin screen.
// Stripe shows as fully onboarded + charges/payouts enabled so the payout-blocked
// banner never fires and the Stripe tab looks live.
export function demoClient() {
  return {
    id: DEMO_CLIENT_ID,
    name: DEMO_CLIENT_NAME,
    internal: false,
    contactName: DEMO_CLIENT.contactName,
    contactEmail: DEMO_CLIENT.contactEmail,
    contactPhone: DEMO_CLIENT.contactPhone,
    notes: DEMO_CLIENT.notes,
    active: true,
    timezone: DEMO_TIMEZONE,
    accentColor: DEMO_ACCENT,
    logoUrl: DEMO_CLIENT.logoUrl,
    iconUrl: DEMO_CLIENT.iconUrl,
    features: DEMO_FEATURES,
    stripeAccountId: DEMO_CLIENT.stripeAccountId,
    onboardingStatus: 'charges_enabled',
    chargesEnabled: true,
    payoutsEnabled: true,
    requirementsCurrentlyDue: [],
    onboardingStatusUpdatedAt: isoDaysFromNow(-45),
    platformFeeBps: DEMO_CLIENT.finance.platformFeeBps,
    platformFeeFlatCents: DEMO_CLIENT.platformFeeFlatCents,
    customerFees: DEMO_CLIENT.customerFees,
    feeModelConfig: DEMO_CLIENT.feeModelConfig,
    createdAt: isoDaysFromNow(-180),
    updatedAt: isoDaysFromNow(-2),
  }
}

// ClientLite array shape for GET /api/clients (AveryWidget / Zones picker).
export function demoClientsList() {
  return [demoClient()]
}
